import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  LocalSectionFields,
  emptyLocalSection,
  localFieldsFrom,
  localSectionFrom,
  type FooterField,
  type LocalSectionValue,
} from '../components/LocalSectionFields.js';
import {
  OutgoingLinksField,
  emptyOutgoingLinks,
  linkFieldsFrom,
  type OutgoingLinksValue,
  type SecondaryRowErrors,
} from '../components/OutgoingLinksField.js';
import { SubmitRefusalLine, useSubmitRefusal } from '../components/SubmitRefusal.js';
import { RefusalNotice, useWritesRefused } from '../components/SuspendedNotice.js';
import { Alert, Button, Card, SelectField, TextField } from '../components/ui.js';
import { ApiError } from '../lib/api.js';
import { FIELD_LIMITS } from '../lib/fieldLimits.js';
import { formatCredits } from '../lib/format.js';
import {
  fetchClientProfiles,
  fetchOrderTypes,
  placeOrder,
  type ClientProfile,
  type OrderIntake,
  type OrderTypeOption,
} from '../lib/ordersApi.js';
import { useSession } from '../session/SessionProvider.js';

/**
 * The order form — **intake v2**.
 *
 * The governing principle is the operator's: the form asks only for what is
 * actually used to create the site. Two answers are required, the target's name
 * and its main URL, and everything else on the page is optional.
 *
 * Round 1 asked for services, service locations, USPs, brand constraints,
 * things to avoid, footer requirements and target queries. None of those are
 * here. Their columns keep every value already stored; they simply stopped
 * being collected, because they were not inputs to anything.
 *
 * A target is not always a business. It may be a client's business, an
 * affiliate site, a brand or a social profile, and no copy on this page assumes
 * otherwise.
 *
 * Renewals are NOT here. They start from the site (C-1); the extension option
 * survives on this page for discoverability and routes into that flow.
 */

interface FormState {
  targetName: string;
  mainUrl: string;
  links: OutgoingLinksValue;
  local: LocalSectionValue;
}

const EMPTY: FormState = {
  targetName: '',
  mainUrl: '',
  links: emptyOutgoingLinks(),
  local: emptyLocalSection(),
};

/**
 * A project prefills target information and the local section. It prefills no
 * link choices, because there are none to prefill: links are decided per order
 * and are never stored on the reusable project profile.
 */
function fromProject(project: ClientProfile): FormState {
  return {
    targetName: project.businessName,
    mainUrl: project.websiteUrl ?? '',
    links: emptyOutgoingLinks(),
    local: localSectionFrom(project),
  };
}

function toIntake(form: FormState): OrderIntake {
  const local = localFieldsFrom(form.local);

  return {
    targetName: form.targetName.trim(),
    mainUrl: form.mainUrl.trim(),
    ...linkFieldsFrom(form.links, form.mainUrl),
    local: {
      ...(local.targetLocation === '' ? {} : { targetLocation: local.targetLocation }),
      footer: local.footer,
    },
  };
}

/* --------------------------------------------------------- server errors --- */

interface FormErrors {
  targetName?: string;
  mainUrl?: string;
  homeUrl?: string;
  homeWording?: string;
  secondary?: Record<number, SecondaryRowErrors>;
  links?: string;
  targetLocation?: string;
  footer?: Partial<Record<FooterField, string>>;
}

const FOOTER_FIELDS = new Set<string>([
  'businessName',
  'phone',
  'addressLine1',
  'addressLine2',
  'city',
  'region',
  'postcode',
  'country',
  'mapsEmbed',
]);

/**
 * Server field paths, mapped back onto the control the person is looking at.
 *
 * Paths arrive as `intake.local.footer.mapsEmbed` or `intake.secondaryLinks.0.url`,
 * so this walks the segments rather than reading only the first one.
 */
function errorsFrom(issues: { field: string; message: string }[]): FormErrors {
  const errors: FormErrors = {};

  for (const issue of issues) {
    const path = issue.field.split('.');
    const segments = path[0] === 'intake' ? path.slice(1) : path;
    const [head, second, third] = segments;

    if (head === 'targetName') errors.targetName = issue.message;
    else if (head === 'mainUrl') errors.mainUrl = issue.message;
    else if (head === 'homeLinkUrl') errors.homeUrl = issue.message;
    else if (head === 'homeLinkWording') errors.homeWording = issue.message;
    else if (head === 'linkMode') errors.links = issue.message;
    else if (head === 'secondaryLinks') {
      const index = Number(second);
      if (Number.isInteger(index)) {
        /*
         * C-61. `secondaryLinks.0.url` and `secondaryLinks.0.wording` land under
         * their own boxes. A message about the row as a whole has no field, and
         * goes under the URL: the address is what makes it a row at all.
         */
        const field = third === 'wording' ? 'wording' : 'url';
        errors.secondary = {
          ...errors.secondary,
          [index]: { ...errors.secondary?.[index], [field]: issue.message },
        };
      } else {
        errors.links = issue.message;
      }
    } else if (head === 'local') {
      if (second === 'targetLocation') errors.targetLocation = issue.message;
      else if (second === 'footer' && third !== undefined && FOOTER_FIELDS.has(third)) {
        errors.footer = { ...errors.footer, [third]: issue.message };
      }
    }
  }

  return errors;
}

export function NewOrderPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { session, refresh } = useSession();
  // Suspended, or an operator viewing read-only - WAIT-B defect 3.
  const { refused: suspended } = useWritesRefused();
  const [searchParams] = useSearchParams();

  const [projects, setProjects] = useState<ClientProfile[]>([]);
  const [orderTypes, setOrderTypes] = useState<OrderTypeOption[]>([]);
  const [loading, setLoading] = useState(true);

  const [projectId, setProjectId] = useState(
    searchParams.get('clientProfileId') ?? searchParams.get('projectId') ?? '',
  );
  const [form, setForm] = useState<FormState>(EMPTY);
  const [extraYears, setExtraYears] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // WAIT-PL ruling 1: a refusal is shown at the button and moves to the field.
  const refusal = useSubmitRefusal();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [projectsResponse, typesResponse] = await Promise.all([
          fetchClientProfiles(),
          fetchOrderTypes(),
        ]);
        if (cancelled) return;
        setProjects(projectsResponse.clientProfiles);
        setOrderTypes(typesResponse.orderTypes);

        // A project named in the URL prefills the form straight away.
        const preselected = searchParams.get('clientProfileId') ?? searchParams.get('projectId');
        const match = projectsResponse.clientProfiles.find((p) => p.id === preselected);
        if (match) setForm(fromProject(match));
      } catch {
        if (!cancelled) setError('We could not load the order form.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const buildType = orderTypes.find((type) => type.type === 'site_build');
  const extensionType = orderTypes.find((type) => type.type === 'year_extension');
  const available = session?.credits?.availableCredits ?? 0;

  const baseCost = buildType?.credits ?? 150;
  const perYear = buildType?.creditsPerYear ?? 50;
  const maxExtraYears = Math.max((buildType?.maxYears ?? 6) - 1, 0);
  const cost = baseCost + perYear * extraYears;
  const affordable = available >= cost;

  function update(field: 'targetName' | 'mainUrl') {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setForm((current) => ({ ...current, [field]: event.target.value }));
    };
  }

  function chooseProject(id: string): void {
    setProjectId(id);
    const match = projects.find((project) => project.id === id);
    // Choosing "A new project" clears the form rather than leaving the last
    // project's details sitting under a heading that says otherwise.
    setForm(match ? fromProject(match) : EMPTY);
    setFieldErrors({});
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    refusal.clear();
    setBusy(true);

    try {
      const response = await placeOrder({
        ...(projectId ? { clientProfileId: projectId } : {}),
        intake: toIntake(form),
        orderType: 'site_build',
        years: 1 + extraYears,
      });
      await refresh();
      void navigate(`/orders/${response.order.id}`, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.fieldIssues.length > 0) {
        const mapped = errorsFrom(caught.fieldIssues);
        setFieldErrors(mapped);
        if (Object.keys(mapped).length > 0) {
          setError('Please check the highlighted fields.');
          refusal.refuseFields();
        } else {
          setError(caught.message);
          refusal.refuse(caught.message);
        }
      } else {
        const sentence =
          caught instanceof ApiError ? caught.message : 'We could not place your order.';
        setError(sentence);
        refusal.refuse(sentence);
      }
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <p className="text-sm text-fg-muted" role="status">
        Loading…
      </p>
    );
  }

  /*
   * P-4, and **this is the one that has to be here rather than on the links.**
   * Every "Place an order" link is hidden for a suspended agency, but a
   * bookmark, a back button, or a link in an old email all reach this page
   * directly. Refusing at the form is what makes hiding the links a tidiness
   * measure rather than the whole of the protection - and the server's 403 is
   * what makes even this a courtesy.
   */
  if (suspended) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Order a site build</h1>
        </div>
        <RefusalNotice action="order a build" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Order a site build</h1>
        <p className="mt-1 text-sm text-fg">Two answers start a build. The rest is optional.</p>
        <p className="mt-1 text-sm text-fg-muted">
          You have {formatCredits(available)} available to spend.
        </p>
      </div>

      {extensionType ? (
        <Alert tone="info">
          Keeping a site you already have online? That starts from the site itself.{' '}
          <Link to="/sites" className="underline">
            Go to your sites
          </Link>
          .
        </Alert>
      ) : null}

      <form
        ref={refusal.formRef}
        onSubmit={(event) => void handleSubmit(event)}
        className="space-y-6"
        noValidate
      >
        {error ? <Alert>{error}</Alert> : null}

        <Card className="space-y-4">
          <h2 className="text-sm font-semibold text-fg-muted">Project</h2>
          <SelectField
            label="Project"
            hint="Every order belongs to a project. Pick an existing one or start new."
            id="clientProfileId"
            name="clientProfileId"
            value={projectId}
            onChange={(event) => chooseProject(event.target.value)}
          >
            <option value="">A new project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.businessName}
              </option>
            ))}
          </SelectField>
        </Card>

        <Card className="space-y-5">
          <div>
            <h2 className="text-sm font-semibold text-fg-muted">Target information</h2>
            <p className="mt-1 text-sm text-fg-muted">
              What we need to know about the brand or site this build is for.
            </p>
          </div>

          <TextField
            label="Name"
            hint="The brand, business, or site this build is targeting."
            name="targetName"
            required
            maxLength={FIELD_LIMITS.businessName}
            value={form.targetName}
            onChange={update('targetName')}
            error={fieldErrors.targetName}
          />

          <TextField
            label="Main URL"
            hint="Website, store, or profile."
            name="mainUrl"
            required
            placeholder="https://harbourpointplumbing.example"
            value={form.mainUrl}
            onChange={update('mainUrl')}
            error={fieldErrors.mainUrl}
          />
        </Card>

        <Card>
          <OutgoingLinksField
            value={form.links}
            onChange={(links) => setForm((current) => ({ ...current, links }))}
            mainUrl={form.mainUrl}
            targetName={form.targetName}
            errors={{
              homeUrl: fieldErrors.homeUrl,
              homeWording: fieldErrors.homeWording,
              secondary: fieldErrors.secondary,
              links: fieldErrors.links,
            }}
          />
        </Card>

        <Card className="space-y-5">
          <div>
            <h2 className="text-sm font-semibold text-fg-muted">Local business?</h2>
            <p className="mt-1 text-sm text-fg-muted">
              Give a target location and we build the site around it.
            </p>
          </div>

          <LocalSectionFields
            value={form.local}
            onChange={(local) => setForm((current) => ({ ...current, local }))}
            targetName={form.targetName}
            errors={{ targetLocation: fieldErrors.targetLocation, footer: fieldErrors.footer }}
          />
        </Card>

        <Card className="space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-fg-muted">How long should it stay live?</h2>
            <p className="mt-1 text-sm text-fg-muted">
              The build includes the first year of hosting and domain registration. Extra years
              renew both. Add them now, or at any time later.
            </p>
          </div>
          <SelectField
            label="Extra years"
            id="extraYears"
            name="extraYears"
            value={String(extraYears)}
            onChange={(event) => setExtraYears(Number(event.target.value))}
          >
            {Array.from({ length: maxExtraYears + 1 }, (_, years) => (
              <option key={years} value={years}>
                {years === 0
                  ? 'Just the first year (included)'
                  : `+${years} extra ${years === 1 ? 'year' : 'years'} · ${
                      perYear * years
                    } credits`}
              </option>
            ))}
          </SelectField>
        </Card>

        <Card>
          <h2 className="text-sm font-semibold text-fg-muted">Before you confirm</h2>
          <p className="mt-3 text-sm text-fg">
            This order will set aside <strong>{formatCredits(cost)}</strong>
            {extraYears > 0
              ? ` · ${formatCredits(baseCost)} for the build and first year, plus ${formatCredits(
                  perYear * extraYears,
                )} for ${extraYears} more ${extraYears === 1 ? 'year' : 'years'}`
              : ''}
            . They are not spent until the site goes live, and you get them back if the order is
            cancelled.
          </p>
          <p className="mt-2 text-sm text-fg-muted">
            After this order you would have {formatCredits(Math.max(available - cost, 0))}{' '}
            available.
          </p>
          {!affordable ? (
            <div className="mt-4">
              <Alert>
                You need {formatCredits(cost)} for this order and have {formatCredits(available)}{' '}
                available.{' '}
                <Link to="/credits" className="underline">
                  Buy more credits
                </Link>
                .
              </Alert>
            </div>
          ) : null}
        </Card>

        <SubmitRefusalLine message={refusal.message} />

        <div className="flex items-center gap-3">
          <Button type="submit" busy={busy} disabled={!affordable}>
            Place order
          </Button>
          <Link to="/orders" className="text-sm text-fg-muted underline">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
